import axios from "axios";

/**
 * Activity templates — the masters used when reaching out to a lead from a
 * lead activity, and the send endpoints themselves.
 *
 * Backend app: wsgi/teson/activitytemplate. Mounted at the API root, so no
 * `/api` prefix here (same as the rest of this client).
 */

const BASE = "/activity-templates";

// Every list endpoint is paginated; callers only ever want the rows.
export const rowsOf = (response) => {
  const data = response?.data;
  if (Array.isArray(data)) return data;
  return data?.results || [];
};

const query = (params = {}) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      search.append(key, value);
    }
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

// -- lookups shared with other features --------------------------------

export const loadProducts = () => axios.get(`/payment/pr/${query({ page_size: 500 })}`);

export const loadBusinessNumbers = () =>
  axios.get(`/whatsapp_api/business-numbers/${query({ page_size: 200 })}`);

// Templates Meta has approved and whatsapp_api has synced.
export const loadMetaTemplates = (params) =>
  axios.get(`/whatsapp_api/whatsapp-templates/${query({ page_size: 500, ...params })}`);

// -- template categories -----------------------------------------------

export const loadCategories = (params) =>
  axios.get(`${BASE}/categories/${query({ page_size: 500, ...params })}`);

export const createCategory = (payload) => axios.post(`${BASE}/categories/`, payload);
export const updateCategory = (id, payload) => axios.patch(`${BASE}/categories/${id}/`, payload);
export const deleteCategory = (id) => axios.delete(`${BASE}/categories/${id}/`);

// -- email templates ----------------------------------------------------

export const loadEmailTemplates = (params) =>
  axios.get(`${BASE}/email/${query({ page_size: 500, ...params })}`);

export const createEmailTemplate = (payload) => axios.post(`${BASE}/email/`, payload);
export const updateEmailTemplate = (id, payload) => axios.patch(`${BASE}/email/${id}/`, payload);
export const deleteEmailTemplate = (id) => axios.delete(`${BASE}/email/${id}/`);

/** Render a template for a lead. Pass overrides to preview an unsaved tweak. */
export const previewEmailTemplate = (id, payload) =>
  axios.post(`${BASE}/email/${id}/preview/`, payload);

// -- whatsapp templates -------------------------------------------------

export const loadWhatsappTemplates = (params) =>
  axios.get(`${BASE}/whatsapp/${query({ page_size: 500, ...params })}`);

export const createWhatsappTemplate = (payload) => axios.post(`${BASE}/whatsapp/`, payload);
export const updateWhatsappTemplate = (id, payload) =>
  axios.patch(`${BASE}/whatsapp/${id}/`, payload);
/** Pass `{ fromMeta: true }` to also delete it from the WhatsApp Business Account. */
export const deleteWhatsappTemplate = (id, { fromMeta = false } = {}) =>
  axios.delete(`${BASE}/whatsapp/${id}/${fromMeta ? "?delete_from_meta=true" : ""}`);

/**
 * Two-way sync with Meta for every configured WhatsApp Business Account:
 * submits drafts/edits, pulls templates + statuses, links new ones to products.
 * -> `{results: [{number, pushed, push_errors, created, updated, removed, linked, error}]}`
 */
export const syncWhatsappTemplates = () => axios.post(`${BASE}/whatsapp/sync/`);

export const previewWhatsappTemplate = (id, payload) =>
  axios.post(`${BASE}/whatsapp/${id}/preview/`, payload);

/**
 * Store a header file (image / video / document) for a template.
 * -> `{path, filename, mime_type, size, url}`; save that object on the
 * template as its header media. `url` needs auth, so load it with
 * `useAuthedMedia`.
 */
export const uploadWhatsappMedia = (file, headerType) => {
  const form = new FormData();
  form.append("file", file);
  form.append("header_type", headerType);
  return axios.post(`${BASE}/whatsapp/media/`, form);
};

// -- per-product sending configurations ---------------------------------

export const loadEmailConfigurations = (params) =>
  axios.get(`${BASE}/email-configurations/${query({ page_size: 500, ...params })}`);

export const createEmailConfiguration = (payload) =>
  axios.post(`${BASE}/email-configurations/`, payload);
export const updateEmailConfiguration = (id, payload) =>
  axios.patch(`${BASE}/email-configurations/${id}/`, payload);
export const deleteEmailConfiguration = (id) =>
  axios.delete(`${BASE}/email-configurations/${id}/`);
export const setDefaultEmailConfiguration = (id) =>
  axios.post(`${BASE}/email-configurations/${id}/set-default/`);
/** Sends synchronously; a 400 carries the SMTP server's error in `message`. */
export const sendTestEmail = (id, payload) =>
  axios.post(`${BASE}/email-configurations/${id}/send-test/`, payload);

/**
 * Everything sent and received through a mailbox, newest first: inbox mail
 * plus activity emails, including ones that failed.
 * Params: `{direction, status, q, date_from, date_to, page, page_size}`.
 * -> `{count, page, page_size, results, summary: {total, inbound, outbound, failed}}`
 */
export const loadEmailConfigurationLogs = (id, params) =>
  axios.get(`${BASE}/email-configurations/${id}/logs/${query(params)}`);

export const loadWhatsappConfigurations = (params) =>
  axios.get(`${BASE}/whatsapp-configurations/${query({ page_size: 500, ...params })}`);

export const createWhatsappConfiguration = (payload) =>
  axios.post(`${BASE}/whatsapp-configurations/`, payload);
export const updateWhatsappConfiguration = (id, payload) =>
  axios.patch(`${BASE}/whatsapp-configurations/${id}/`, payload);
export const deleteWhatsappConfiguration = (id) =>
  axios.delete(`${BASE}/whatsapp-configurations/${id}/`);
export const setDefaultWhatsappConfiguration = (id) =>
  axios.post(`${BASE}/whatsapp-configurations/${id}/set-default/`);
/** Checks the number's credentials with Meta; sends nothing. */
export const testWhatsappConnection = (id) =>
  axios.post(`${BASE}/whatsapp-configurations/${id}/test-connection/`);
/** `{to_number, template_name?, language?}` or `{to_number, text}`. */
export const sendTestWhatsapp = (id, payload) =>
  axios.post(`${BASE}/whatsapp-configurations/${id}/send-test/`, payload);

/** A number's messages both ways, plus sends that never reached Meta. Same shape as above. */
export const loadWhatsappConfigurationLogs = (id, params) =>
  axios.get(`${BASE}/whatsapp-configurations/${id}/logs/${query(params)}`);

// -- sending ------------------------------------------------------------

/** Queues one email and logs a LeadActivity. One lead per call. */
export const sendActivityEmail = (payload) => axios.post(`${BASE}/send/email/`, payload);

/** Queues one WhatsApp template message and logs a LeadActivity. */
export const sendActivityWhatsapp = (payload) => axios.post(`${BASE}/send/whatsapp/`, payload);

export const loadMessages = (params) =>
  axios.get(`${BASE}/messages/${query({ page_size: 100, ...params })}`);

/** The `{{ placeholders }}` a template author can use. */
export const loadVariables = () => axios.get(`${BASE}/variables/`);

// -- bulk sends -----------------------------------------------------------

/**
 * Start a bulk send. Returns at once (202) with the batch; the server renders
 * and sends in the background, so the browser can be closed.
 * Payload: `{channel, record_type, record_ids, template, name?, subject?,
 * html_content?, variables?, email_configuration?, business_number?,
 * activity_status?, notes?}`.
 */
export const createBatch = (payload) => axios.post(`${BASE}/batches/`, payload);

/** Params: `{channel, status, created_by, search, page, page_size}`. */
export const loadBatches = (params) =>
  axios.get(`${BASE}/batches/${query({ page_size: 20, ...params })}`);

export const loadBatch = (id) => axios.get(`${BASE}/batches/${id}/`);

/**
 * A batch's messages. Params: `{state: queued|retrying|sent|failed,
 * failure_kind, q, page, page_size}`.
 */
export const loadBatchMessages = (id, params) =>
  axios.get(`${BASE}/batches/${id}/messages/${query({ page_size: 50, ...params })}`);

/** `{ids?: [...], include_permanent?: bool}` -> `{retried, batch}`. */
export const retryBatch = (id, payload = {}) =>
  axios.post(`${BASE}/batches/${id}/retry/`, payload);

/** CSV of the batch (same filters as loadBatchMessages), as a Blob. */
export const exportBatch = (id, params) =>
  axios.get(`${BASE}/batches/${id}/export/${query(params)}`, { responseType: "blob" });
